/**
 * Local Links Subscription System
 * ================================
 *
 * EXTENSIBLE TIER & PERMISSION ARCHITECTURE
 *
 * To add a new tier:
 *   1. Add to TIERS config below
 *   2. That's it! Types auto-propagate.
 *
 * To add a new permission:
 *   1. Add to PERMISSIONS config below
 *   2. That's it! Types auto-propagate.
 *
 * To add a new limit:
 *   1. Add to LIMITS config below
 *   2. That's it! Types auto-propagate.
 */

// ============================================================================
// TIER CONFIGURATION
// ============================================================================

/**
 * Define all subscription tiers here.
 * Order matters: lower index = lower tier level
 */
export const TIERS = {
  free: {
    level: 0,
    name: "Free",
    description: "Browse courses and view reviews",
    price: { monthly: 0, yearly: 0 },
    stripePriceIds: {
      monthly: null,
      yearly: null,
    },
    badge: { variant: "outline" as const, icon: null },
  },
  member: {
    level: 1,
    name: "Member",
    description: "Full social access with smart limits",
    price: { monthly: 8.99, yearly: 89 },
    stripePriceIds: {
      monthly: process.env.STRIPE_MEMBER_MONTHLY_PRICE_ID || null,
      yearly: process.env.STRIPE_MEMBER_YEARLY_PRICE_ID || null,
    },
    badge: { variant: "default" as const, icon: "Zap" },
  },
} as const;

// Auto-derive TierName type from config
export type TierName = keyof typeof TIERS;

// Get ordered tier names (for comparison)
export const TIER_ORDER: TierName[] = (
  Object.entries(TIERS) as [TierName, (typeof TIERS)[TierName]][]
)
  .sort((a, b) => a[1].level - b[1].level)
  .map(([name]) => name);

// ============================================================================
// LEGACY TYPE MIGRATION
// ============================================================================

/**
 * Map legacy subscription types to modern tier names.
 * Used for backward compatibility during migration.
 */
export const LEGACY_TYPE_TO_TIER_MAP: Record<string, TierName> = {
  // Legacy subscription types
  league_manager: "member",
  clubmaster: "member",
  both: "member",
  // Old tier names (renamed)
  player: "member",
  pro: "member",
} as const;

/**
 * Normalize any subscription type/tier to a valid TierName.
 * Handles legacy types, old tier names, and invalid values.
 *
 * @param value - The subscription type, tier name, or legacy value
 * @returns A valid TierName (defaults to "free" for unknown values)
 */
export function normalizeTier(value: string | null | undefined): TierName {
  if (!value) return "free";

  // Check if it's already a valid tier name
  if (value in TIERS) {
    return value as TierName;
  }

  // Check if it's a legacy type that maps to a tier
  const normalizedValue = value.toLowerCase().replace("-", "_");
  if (normalizedValue in LEGACY_TYPE_TO_TIER_MAP) {
    return LEGACY_TYPE_TO_TIER_MAP[normalizedValue];
  }

  // Default to free for unknown values
  return "free";
}

// ============================================================================
// PERMISSIONS CONFIGURATION
// ============================================================================

/**
 * Define all permissions and which tier unlocks them.
 * `minTier` is the minimum tier required for this permission.
 * Set to the highest tier name to disable for lower tiers.
 */
export const PERMISSIONS = {
  // Content viewing
  viewBasicCourseInfo: {
    minTier: "free" as TierName,
    description: "View basic course information",
  },
  viewCourseReviews: {
    minTier: "free" as TierName,
    description: "View course reviews and ratings",
  },
  viewOfficialPosts: {
    minTier: "free" as TierName,
    description: "View official clubhouse announcements",
  },
  viewPublicLobbies: {
    minTier: "free" as TierName,
    description: "Browse and view public lobbies",
  },
  joinLobbies: {
    minTier: "free" as TierName,
    description: "Join public lobbies",
  },
  viewCommunityPosts: {
    minTier: "free" as TierName, // Freemium: Let free users see feed (read-only)
    description: "View community posts in social feed",
  },
  viewSocialFeed: {
    minTier: "free" as TierName, // Freemium: Read-only access drives FOMO
    description: "Access the full social feed",
  },
  viewPlayerProfiles: {
    minTier: "free" as TierName, // Freemium: Browse profiles, gate connections
    description: "View other player profiles",
  },
  viewLocalTournaments: {
    minTier: "free" as TierName, // Freemium: Show local activity
    description: "View local tournaments",
  },
  viewRegionalTournaments: {
    minTier: "member" as TierName,
    description: "View regional tournaments",
  },

  // Social actions
  post: {
    minTier: "member" as TierName,
    description: "Post content to your profile",
  },
  challenge: {
    minTier: "free" as TierName, // Freemium: 1/week limit for free, 5/week for member
    description: "Send challenges to other golfers",
  },
  message: {
    minTier: "member" as TierName,
    description: "Send direct messages",
  },
  messageAnyone: {
    minTier: "member" as TierName,
    description: "Message anyone",
  },
  tagEntities: {
    minTier: "member" as TierName,
    description: "Tag courses, clubs, and leagues in posts",
  },

  // Discovery
  discover: {
    minTier: "free" as TierName, // Freemium: 10mi radius for free, 25mi for member
    description: "Discover and find other golfers",
  },
  advancedFilters: {
    minTier: "member" as TierName,
    description: "Use advanced discovery filters",
  },

  // Lobbies
  createLobbies: {
    minTier: "member" as TierName,
    description: "Create and host lobbies",
  },

  // Leagues
  joinLeagues: {
    minTier: "free" as TierName, // Freemium: 1 league limit for free
    description: "Join golf leagues",
  },
  createLeagues: {
    minTier: "member" as TierName,
    description: "Create and manage golf leagues",
  },

  // Premium features (now included in Member tier)
  priorityMatching: {
    minTier: "member" as TierName,
    description: "Get matched faster",
  },
  adFree: { minTier: "member" as TierName, description: "Ad-free experience" },
  analytics: {
    minTier: "member" as TierName,
    description: "View performance analytics",
  },

  // Club Swap Marketplace
  clubSwapListing: {
    minTier: "member" as TierName,
    description: "List items on Club Swap marketplace",
  },
  clubSwapContact: {
    minTier: "member" as TierName,
    description: "Contact sellers on Club Swap",
  },
} as const;

// Auto-derive PermissionName type from config
export type PermissionName = keyof typeof PERMISSIONS;

// ============================================================================
// LIMITS CONFIGURATION
// ============================================================================

/**
 * Define usage limits per tier.
 * Use -1 for unlimited.
 * Use 0 to disable.
 */
export const LIMITS = {
  weeklyChallenges: {
    description: "Challenges per week",
    values: { free: 1, member: 5 }, // Freemium: 1/week hook
  },
  weeklyPosts: {
    description: "Posts per week",
    values: { free: 0, member: 10 }, // Member only - free users can view but not post
  },
  weeklyMessages: {
    description: "Messages per week",
    values: { free: 0, member: -1 }, // Keep as Member gate
  },
  discoveryRadiusMiles: {
    description: "Discovery radius in miles",
    values: { free: 10, member: 25 }, // Freemium: 10mi for free
  },
  maxLeagues: {
    description: "Maximum league memberships",
    values: { free: 1, member: -1 }, // Freemium: 1 league for free
  },
  monthlyMatches: {
    description: "Matches per month",
    values: { free: 0, member: -1 },
  },
  storageGB: {
    description: "Media storage in GB",
    values: { free: 0, member: 50 },
  },
  monthlyClubSwapListings: {
    description: "Club Swap listings per month",
    values: { free: 0, member: -1 },
  },
  maxLobbies: {
    description: "Concurrent lobbies hosted",
    values: { free: 0, member: -1 },
  },
} as const;

// Auto-derive LimitName type from config
export type LimitName = keyof typeof LIMITS;

// ============================================================================
// UPGRADE PROMPTS
// ============================================================================

/**
 * Upgrade prompts by action and tier.
 * Used for contextual upgrade CTAs.
 */
export const UPGRADE_PROMPTS: Record<
  string,
  Partial<Record<TierName, string>>
> = {
  challenge: {
    free: "Upgrade to Member for challenges",
  },
  post: {
    free: "Upgrade to Member to share your golf content",
  },
  message: {
    free: "Upgrade to Member to message other golfers",
  },
  discover: {
    free: "Upgrade to Member to discover local golfers",
  },
  socialFeed: {
    free: "Upgrade to Member to view the community feed",
  },
  tournaments: {
    free: "Upgrade to Member to view tournaments",
  },
  leagues: {
    free: "Upgrade to Member to join leagues",
  },
  clubSwapListing: {
    free: "Upgrade to Member to list items on Club Swap",
  },
  clubSwapContact: {
    free: "Upgrade to Member to contact sellers on Club Swap",
  },
  createLobbies: {
    free: "Upgrade to Member to create and host lobbies",
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get tier configuration
 */
export function getTier(tierName: TierName) {
  return TIERS[tierName];
}

/**
 * Get tier level (for comparison)
 */
export function getTierLevel(tierName: TierName): number {
  return TIERS[tierName]?.level ?? 0;
}

/**
 * Check if userTier meets or exceeds requiredTier
 */
export function meetsMinimumTier(
  userTier: TierName,
  requiredTier: TierName
): boolean {
  return getTierLevel(userTier) >= getTierLevel(requiredTier);
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(
  userTier: TierName,
  permission: PermissionName
): boolean {
  const permConfig = PERMISSIONS[permission];
  if (!permConfig) return false;
  return meetsMinimumTier(userTier, permConfig.minTier);
}

/**
 * Get all permissions for a tier
 */
export function getTierPermissions(
  tierName: TierName
): Record<PermissionName, boolean> {
  const result = {} as Record<PermissionName, boolean>;
  for (const [key, config] of Object.entries(PERMISSIONS)) {
    result[key as PermissionName] = meetsMinimumTier(tierName, config.minTier);
  }
  return result;
}

/**
 * Get limit value for a tier
 */
export function getLimit(tierName: TierName, limitName: LimitName): number {
  return LIMITS[limitName]?.values[tierName] ?? 0;
}

/**
 * Get all limits for a tier
 */
export function getTierLimits(tierName: TierName): Record<LimitName, number> {
  const result = {} as Record<LimitName, number>;
  for (const [key, config] of Object.entries(LIMITS)) {
    result[key as LimitName] = config.values[tierName] ?? 0;
  }
  return result;
}

/**
 * Check if a limit is unlimited (-1)
 */
export function isUnlimited(limit: number): boolean {
  return limit === -1;
}

/**
 * Calculate remaining from limit and used
 */
export function getRemainingLimit(used: number, limit: number): number {
  if (isUnlimited(limit)) return -1;
  return Math.max(0, limit - used);
}

/**
 * Check if user is within their limit
 */
export function isWithinLimit(used: number, limit: number): boolean {
  if (isUnlimited(limit)) return true;
  if (limit === 0) return false;
  return used < limit;
}

/**
 * Get upgrade prompt for an action
 */
export function getUpgradePrompt(
  action: string,
  tierName: TierName
): string | null {
  return UPGRADE_PROMPTS[action]?.[tierName] ?? null;
}

/**
 * Get the next tier upgrade option
 */
export function getNextTier(currentTier: TierName): TierName | null {
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  if (currentIndex === -1 || currentIndex >= TIER_ORDER.length - 1) {
    return null;
  }
  return TIER_ORDER[currentIndex + 1];
}

/**
 * Get price info for a tier
 */
export function getTierPricing(tierName: TierName) {
  const tier = TIERS[tierName];
  return {
    monthly: tier.price.monthly,
    yearly: tier.price.yearly,
    yearlySavings:
      tier.price.monthly > 0
        ? Math.round((1 - tier.price.yearly / (tier.price.monthly * 12)) * 100)
        : 0,
  };
}

/**
 * Get Stripe price ID for tier and interval
 */
export function getStripePriceId(
  tierName: TierName,
  interval: "monthly" | "yearly"
): string | null {
  return TIERS[tierName]?.stripePriceIds[interval] ?? null;
}

// ============================================================================
// TYPE EXPORTS FOR COMPONENTS
// ============================================================================

/**
 * Tier features summary (for UI display)
 */
export interface TierFeatures {
  tier: TierName;
  name: string;
  description: string;
  pricing: ReturnType<typeof getTierPricing>;
  permissions: Record<PermissionName, boolean>;
  limits: Record<LimitName, number>;
}

/**
 * Get complete tier features (for pricing pages, etc.)
 */
export function getTierFeatures(tierName: TierName): TierFeatures {
  const tier = TIERS[tierName];
  return {
    tier: tierName,
    name: tier.name,
    description: tier.description,
    pricing: getTierPricing(tierName),
    permissions: getTierPermissions(tierName),
    limits: getTierLimits(tierName),
  };
}

/**
 * Get all tiers with features (for comparison tables)
 */
export function getAllTierFeatures(): TierFeatures[] {
  return TIER_ORDER.map(getTierFeatures);
}

/**
 * Usage check result
 */
export interface UsageCheckResult {
  allowed: boolean;
  limit: number;
  used: number;
  remaining: number;
  isUnlimited: boolean;
  upgradePrompt: string | null;
}

/**
 * Check usage against limit with full context
 */
export function checkUsage(
  tierName: TierName,
  limitName: LimitName,
  used: number
): UsageCheckResult {
  const limit = getLimit(tierName, limitName);
  const unlimited = isUnlimited(limit);
  const remaining = getRemainingLimit(used, limit);
  const allowed = isWithinLimit(used, limit);

  // Map limit names to upgrade prompt actions
  const actionMap: Partial<Record<LimitName, string>> = {
    weeklyChallenges: "challenge",
    weeklyPosts: "post",
    weeklyMessages: "message",
    discoveryRadiusMiles: "discover",
    maxLeagues: "leagues",
  };

  const action = actionMap[limitName];
  const upgradePrompt =
    !allowed && action ? getUpgradePrompt(action, tierName) : null;

  return {
    allowed,
    limit,
    used,
    remaining,
    isUnlimited: unlimited,
    upgradePrompt,
  };
}

// ============================================================================
// FEATURE FLAGS (for conditional rendering)
// ============================================================================

/**
 * Feature flag configuration - easy enable/disable of features
 */
export const FEATURE_FLAGS = {
  subscriptions: true,
  freeTrials: true,
  trialDays: 14,
  yearlyDiscounts: true,
  referralProgram: false, // Coming soon
  giftSubscriptions: false, // Coming soon
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

export function isFeatureEnabled(feature: FeatureFlag): boolean {
  return !!FEATURE_FLAGS[feature];
}

// ============================================================================
// COMPATIBILITY LAYER (for legacy code)
// ============================================================================

export type FeatureTier = TierName;

export interface ContentPermissions {
  canViewCommunityPosts: boolean;
  canPost: boolean;
  canViewOutOfStateEvents: boolean;
  canTagCourses: boolean;
}

export const SUBSCRIPTION_TIERS = TIER_ORDER.map((tierName) => {
  const tier = TIERS[tierName];
  return {
    id: tierName as FeatureTier,
    name: tier.name,
    description: tier.description,
    monthlyPrice: tier.price.monthly * 100, // Convert to cents for legacy
    yearlyPrice: tier.price.yearly * 100, // Convert to cents for legacy
    isPopular: tierName === "member",
    ctaText: tierName === "free" ? "Get Started" : "Start 14-Day Free Trial",
    limits: getTierLimits(tierName),
  };
});

// Construct legacy FEATURES array from PERMISSIONS and LIMITS
export const FEATURES_ARRAY = [
  // Core Features (from PERMISSIONS)
  {
    id: "view_courses",
    name: "Browse Courses",
    description: "View course information and reviews",
    category: "core",
    limits: {
      free: { limit: -1, description: "Unlimited" },
      member: { limit: -1, description: "Unlimited" },
    },
  },
  // Lobbies
  {
    id: "lobbies",
    name: "Lobbies",
    description: "Find and join games",
    category: "social",
    limits: {
      free: { limit: -1, description: "Join public lobbies" },
      member: { limit: -1, description: "Create & join unlimited" },
    },
  },
  // Social Feed (Freemium)
  {
    id: "social_feed",
    name: "Social Feed",
    description: "View community posts and updates",
    category: "social",
    limits: {
      free: { limit: -1, description: "Read-only access" }, // Freemium
      member: { limit: -1, description: "Full access + posting" },
    },
  },
  // Leagues (Freemium)
  {
    id: "leagues",
    name: "Leagues",
    description: "Join and participate in golf leagues",
    category: "social",
    limits: {
      free: { limit: 1, description: "Join 1 league" }, // Freemium
      member: { limit: -1, description: "Unlimited leagues" },
    },
  },
  // Discovery (from LIMITS)
  {
    id: "discovery",
    name: "Find Golfers",
    description: "Discover local golfers near you",
    category: "discovery",
    limits: {
      free: { limit: 10, description: "10-mile radius" }, // Freemium
      member: { limit: 25, description: "25-mile radius" },
    },
  },
  // Challenges (from LIMITS)
  {
    id: "challenges",
    name: "Challenges",
    description: "Send challenges to other golfers",
    category: "social",
    limits: {
      free: { limit: 1, description: "1 per week" }, // Freemium hook
      member: { limit: 5, description: "5 per week" },
    },
  },
  // Messaging (from LIMITS)
  {
    id: "messaging",
    name: "Messaging",
    description: "Message other golfers",
    category: "social",
    limits: {
      free: { limit: 0, description: "Not available" },
      member: { limit: -1, description: "Connections only" },
    },
  },
  // Posting (from LIMITS)
  {
    id: "posting",
    name: "Content Posting",
    description: "Post content to your profile",
    category: "content",
    limits: {
      free: { limit: 0, description: "Not available" },
      member: { limit: 10, description: "10 per week" },
    },
  },
  // Tournaments (from PERMISSIONS)
  {
    id: "tournaments",
    name: "Tournament Search",
    description: "Search for tournaments",
    category: "discovery",
    limits: {
      free: { limit: -1, description: "Local only" }, // Freemium
      member: { limit: -1, description: "Local + regional" },
    },
  },
  // Analytics (from PERMISSIONS)
  {
    id: "analytics",
    name: "Analytics",
    description: "View performance analytics",
    category: "premium",
    limits: {
      free: { limit: 0, description: "Not available" },
      member: { limit: -1, description: "Basic stats" },
    },
  },
];

// Export as FEATURES to match legacy import
export { FEATURES_ARRAY as FEATURES };

export const TIER_CONTENT_PERMISSIONS: Record<FeatureTier, ContentPermissions> =
  {
    free: {
      canViewCommunityPosts: PERMISSIONS.viewCommunityPosts.minTier === "free",
      canPost: PERMISSIONS.post.minTier === "free",
      canViewOutOfStateEvents:
        PERMISSIONS.viewRegionalTournaments.minTier === "free",
      canTagCourses: PERMISSIONS.tagEntities.minTier === "free",
    },
    member: {
      canViewCommunityPosts: true,
      canPost: true,
      canViewOutOfStateEvents: true,
      canTagCourses: true,
    },
  };
